import React from "react";
import { useUpdateShipping, useSetCoupon } from "@queries/order";
import { useAuth } from "@context/AuthContext";

export default function OrderTotals({ orderId, order, totals, products }) {
  const auth = useAuth();
  const canUpdateTotals = auth?.hasPermission?.(
    "sale/can_update_order_product_directly"
  );

  const updateShipping = useUpdateShipping(orderId);
  const setCouponMutation = useSetCoupon(orderId);

  const options = totals?.shipping_options || [];
  const rows = totals?.rows || [];

  const parseNum = React.useCallback((v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const s = String(v);
    const m = s.match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : 0;
  }, []);

  const flatOptions = React.useMemo(() => {
    return options.flatMap((g) =>
      g.items.map((it) => ({
        ...it,
        num: parseNum(it.value)
      }))
    );
  }, [options, parseNum]);

  const [shipping, setShipping] = React.useState("");
  const [coupon, setCoupon] = React.useState("");
  const [couponError, setCouponError] = React.useState("");
  const [isRuleDismissed, setIsRuleDismissed] = React.useState(false);

  const subRow = rows.find((r) => r.code === "sub_total");
  const subValue = parseNum(subRow?.value ?? subRow?.text);

  const expectedShipping = subValue < 10 ? "3" : "1";
  const isRuleViolated =
    shipping !== "" &&
    shipping !== expectedShipping &&
    parseNum(shipping) <= 3 &&
    !isRuleDismissed;

  // Reset dismissal when sub-total or shipping changes to a new violation
  React.useEffect(() => {
    setIsRuleDismissed(false);
  }, [subValue]);

  const couponRow = rows.find((r) => r.code === "coupon");
  const defaultCouponCode = React.useMemo(() => {
    const rawCode = order?.customer?.discount_percentage ?? "";
    const normalizedCode = String(rawCode || "").trim();
    return normalizedCode && normalizedCode !== "0" ? normalizedCode : "";
  }, [order?.customer?.discount_percentage]);

  React.useEffect(() => {
    if (!rows.length || flatOptions.length === 0) return;

    const subRow = rows.find((r) => r.code === "sub_total");
    const shipRow = rows.find((r) => r.code === "shipping");
    const taxRow = rows.find((r) => r.code === "tax");
    const discRow =
      rows.find((r) => r.code === "discount") ||
      rows.find((r) => r.code === "coupon");

    const sub = parseNum(subRow?.value ?? subRow?.text);
    const shipNet = parseNum(shipRow?.value ?? shipRow?.text);
    const tax = parseNum(taxRow?.value ?? taxRow?.text);
    const discountAbs = Math.abs(parseNum(discRow?.value ?? discRow?.text) || 0);

    let base = sub - discountAbs + shipNet;
    if (base <= 0) base = sub + shipNet;

    const rate = base > 0 ? tax / base : 0;
    const shipGross = Number((shipNet * (1 + rate)).toFixed(2));

    const EPS = 0.05;
    const match = flatOptions.find((it) => Math.abs(it.num - shipGross) < EPS);

    if (match) {
      setShipping(String(match.value));
    } else if (totals?.shipping?.method) {
      setShipping(String(totals.shipping.method));
    } else {
      setShipping("");
    }
  }, [rows, flatOptions, parseNum, totals?.shipping?.method]);

  React.useEffect(() => {
    if (couponRow) {
      setCoupon("");
      setCouponError("");
    } else {
      setCoupon(defaultCouponCode);
    }
  }, [couponRow, defaultCouponCode]);

  React.useEffect(() => {
    if (couponError && coupon.trim().length === 0) {
      setCouponError("");
    }
  }, [coupon, couponError]);

  const rowsWithCoupon = React.useMemo(() => {
    let result = rows;
    
    // Add Total Items above sub_total
    const totalItems = products?.reduce((sum, p) => sum + ((Number(p.quantity) || 0) * (Number(p.item_count) || 1)), 0) || 0;
    const itemsRow = { code: "total_items", title: "Total Items", text: String(totalItems) };
    
    const subTotalIndex = result.findIndex((r) => r.code === "sub_total");
    if (subTotalIndex !== -1) {
      const copy = [...result];
      copy.splice(subTotalIndex, 0, itemsRow);
      result = copy;
    } else {
      result = [itemsRow, ...result];
    }
    
    if (!canUpdateTotals) return result;

    const hasCoupon = result.some((r) => r.code === "coupon");
    if (hasCoupon) return result;

    const insertIndex = result.findIndex((r) => r.code === "shipping");
    const newRow = { code: "coupon", title: "", text: "" };

    if (insertIndex === -1) {
      return [...result, newRow];
    }

    const copy = [...result];
    copy.splice(insertIndex + 1, 0, newRow);
    return copy;
  }, [rows, canUpdateTotals, products]);

  const handleApplyCoupon = () => {
    if (!canUpdateTotals) return;
    setCouponError("");
    setCouponMutation.mutate(coupon, {
      onSuccess: () => {
        setCouponError("");
      },
      onError: (error) => {
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "Failed to apply coupon.";
        setCouponError(msg);
      }
    });
  };

  const hasExistingCoupon = Boolean(couponRow);
  const applyDisabled =
    !canUpdateTotals ||
    setCouponMutation.isLoading ||
    (!hasExistingCoupon && !coupon.trim());

  const applyButtonLabel = (() => {
    if (setCouponMutation.isLoading) {
      return coupon.trim() ? "Applying..." : "Removing...";
    }
    if (coupon.trim()) {
      return "Apply";
    }
    if (hasExistingCoupon) {
      return "Remove";
    }
    return "Apply";
  })();

  return (
    <div className="card">
      <div className="toolbar">
        <div className="title">Totals</div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {rowsWithCoupon.map((r) => (
              <tr key={r.code}>
                {r.code === "coupon" && canUpdateTotals ? (
                  <>
                    <td style={{ width: "80%" }}>
                      <div
                        style={{
                          textAlign: "right"
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          onClick={handleApplyCoupon}
                          disabled={applyDisabled}
                          style={{ marginLeft: 8 }}
                        >
                          {applyButtonLabel}
                        </button>
                        <input
                          className="input"
                          type="text"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                          placeholder={
                            hasExistingCoupon
                              ? "Leave empty to remove coupon"
                              : "Coupon code"
                          }
                          style={{ maxWidth: 220, textAlign: "right" }}
                        />
                        {r.title}
                        {couponError && (
                          <div
                            style={{
                              color: "#b3261e",
                              fontSize: 12,
                              marginTop: 4
                            }}
                          >
                            {couponError}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="mono">
                      {couponRow?.text ? couponRow.text : ""}
                    </td>
                  </>
                ) : r.code === "coupon" && !canUpdateTotals ? (
                  <>
                    <td style={{ width: "80%", textAlign: "right" }}>
                      {r.title}
                    </td>
                    <td className="mono">{r.text}</td>
                  </>
                ) : r.code === "shipping" && canUpdateTotals ? (
                  <>
                    <td style={{ width: "80%", textAlign: "right" }}>
                      {r.title}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          justifyContent: "flex-start"
                        }}
                      >
                        <select
                          className="select"
                          value={shipping}
                          onChange={(e) => {
                            setShipping(e.target.value);
                            setIsRuleDismissed(false);
                          }}
                          style={isRuleViolated ? { borderColor: "red", borderWidth: "2px", boxShadow: "0 0 5px red" } : {}}
                        >
                          <option value="">— Please Select —</option>
                          {options.map((g, i) => (
                            <optgroup key={i} label={g.group}>
                              {g.items.map((it) => (
                                <option key={it.value} value={it.value}>
                                  {it.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <button
                          className="btn btn-primary"
                          onClick={() => updateShipping.mutate(shipping)}
                          disabled={
                            !shipping || updateShipping.isLoading || !canUpdateTotals
                          }
                        >
                          Save
                        </button>
                      </div>
                      {isRuleViolated && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "8px", textAlign: "left", background: "#fff5f5", padding: "8px", borderRadius: "4px", border: "1px solid #feb2b2" }}>
                          <div style={{ fontWeight: "bold", marginBottom: "4px" }}>⚠️ Delivery Fee Policy:</div>
                          Since sub-total is {subValue.toFixed(2)} JOD, delivery fee should be {expectedShipping}.00 JOD.
                          <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                            <button 
                              className="btn btn-sm" 
                              style={{ backgroundColor: "#38a169", color: "white", border: "none", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                              onClick={() => {
                                setShipping(expectedShipping);
                                setIsRuleDismissed(true);
                                updateShipping.mutate(expectedShipping);
                              }}
                            >
                              Update to {expectedShipping}.00 JOD
                            </button>
                            <button 
                              className="btn btn-sm" 
                              style={{ backgroundColor: "#718096", color: "white", border: "none", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                              onClick={() => {
                                setIsRuleDismissed(true);
                                updateShipping.mutate(shipping);
                              }}
                            >
                              Keep {shipping}.00 JOD
                            </button>
                            <button 
                              className="btn btn-sm" 
                              style={{ backgroundColor: "#e53e3e", color: "white", border: "none", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                              onClick={() => {
                                // Revert to what's in totals if possible
                                const shipRow = rows.find((r) => r.code === "shipping");
                                const shipNet = parseNum(shipRow?.value ?? shipRow?.text);
                                const match = flatOptions.find((it) => Math.abs(it.num - shipNet) < 0.05);
                                if (match) setShipping(String(match.value));
                                setIsRuleDismissed(true);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                ) : r.code === "shipping" && !canUpdateTotals ? (
                  <>
                    <td style={{ width: "80%", textAlign: "right" }}>
                      {r.title}
                    </td>
                    <td className="mono">{r.text}</td>
                  </>
                ) : (
                  <>
                    <td style={{ width: "80%", textAlign: "right" }}>
                      {r.title}
                    </td>
                    <td className="mono">{r.text}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

